import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import Header from './Header';
import InterviewerSection from './InterviewerSection';
import QuestionSection from './QuestionSection';
import useCaseMasterStore from './CaseMasterStore';
import TourStartDialog from 'dialogs/TourStartDialog';
import { CasemasterAssessmentStates, QuestionStates, CaseMasterTourSteps } from './constant';
import { ProctoringContext, TourProviderContext, useLoading, useServices, useSnackbar } from 'contexts';
import { getCaseMasterProblemById, createCaseMasterAttempt, startCaseMasterAttempt, finishCaseMasterAttempt } from 'services';
import { getClientPrefix, Session } from 'utils';
import ActionTimerDialog from 'dialogs/ActionTimerDialog';
import FullscreenPermissionDialog from 'dialogs/FullscreenPermissionDialog';
import AnalysisScreen from './AnalysisScreen';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100vw',
    height: '100vh',
    backgroundColor: '#fff',
    overflow: 'hidden'
  },
  header: {
    height: '76px',
  },
  content: {
    display: 'flex',
    gap: theme.spacing(5),
    height: 'calc(100vh - 76px)',
    justifyContent: 'space-between',
    padding: theme.spacing(0,5),
    overflow: 'auto',
  },
  quesSection: {
    width: 'calc(100vw - 320px)',
    display: 'flex',
    borderLeft: '1px solid',
    borderColor: theme.palette.neutral['clr-200'],
    overflow: 'hidden',
  },
  fullscreenContainer: {
    position: 'relative',
    bottom: '165px',
    margin: '0 auto',
    backgroundColor: '#003E8C',
    color: '#FFFFFF',
  }
}));

const CaseMasterAssessment = () => {
  const classes = useStyles();
  const service = useServices();
  const snackbar = useSnackbar();
  const loading = useLoading();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, tagId } = location.state || {};
  const user = Session.userId;

  const [openTourDialog, setOpenTourDialog] = useState(false);
  const [openFinalSubmittionDialog, setOpenFinalSubmittionDialog] = useState(false);
  const [openFullscreenDialog, setOpenFullscreenDialog] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [finished, setFinished] = useState(false);

  const { setSteps, play } = useContext(TourProviderContext);
  const assessmentState = useCaseMasterStore(state => state.assessmentState);
  const setAssessmentState = useCaseMasterStore(state => state.setAssessmentState);

  const [
    startAssessment, endTutorial, setCasemasterProblem, casemasterProblem,
    setAttempt, attempt, timeOut, questionState, reset,
    setTotalDuration, hasGivenResponse
  ] = useCaseMasterStore(state => [
    state.startAssessment, state.endTutorial, state.setCasemasterProblem, state.casemasterProblem,
    state.setAttempt, state.attempt, state.timeOut, state.questionState, state.reset,
    state.setTotalDuration, state.hasGivenResponse
  ]);

  useEffect(() => {
    reset();
  }, []); // Empty dependency array - only run once on mount

  const {
    endProctoring,
    getProctoringData,
    activateFullScreenProctoring,
    activateGeneralProctoring,
    enableFullscreenMode,
    fullScreenActive
  } = useContext(ProctoringContext);

  const fetchCasemasterProblem = useCallback(async () => {
    const cid = searchParams.get("cid");
    
    try {
      loading.show();

      if (!cid) {
        throw new Error("CaseMaster Problem ID is required");
      }

      const problemData = await getCaseMasterProblemById(cid);  

      const { topic, problem, title, difficulty, otherDetails } = problemData;

      setTotalDuration(1800);
      console.log('Set total duration to 1800 seconds');

      const casemasterProblemObj = {
        _id: cid,
        title: title || 'Case Study Challenge',
        topic: topic || '',
        problem: problem || '',
        description: `<div style="line-height: 1.6;">
          <h3 style="color: #1961BF; margin-bottom: 16px;">Topic: ${topic || 'Case Study'}</h3>
          <p style="margin-bottom: 12px;">You have <strong>30:00 mins</strong> to complete the case study</p>
          
          <div style="background-color: #F5F7FA; padding: 16px; border-radius: 8px; margin: 16px 0;">
            <h4 style="margin-top: 0; margin-bottom: 8px;">Case Introduction:</h4>
            <p style="margin: 0;">${problem || 'No problem description available.'}</p>
          </div>
        </div>`,
        difficulty: difficulty || 'Medium',
        duration: 30,
        entryPoint: data,
        otherDetails: otherDetails || {}
      };

      console.log('Setting casemaster problem:', casemasterProblemObj);
      setCasemasterProblem(casemasterProblemObj);

      // Create attempt after fetching the problem
      console.log('Creating casemaster attempt...');
      const attemptData = {
        user: Session.userId,
        casemasterProblem: cid,
        ...(data === 'contest' && tagId ? { casemasterContest: tagId } : {}),
      };

      const attemptResponse = await createCaseMasterAttempt(attemptData);
      console.log('Attempt created:', attemptResponse);
      
      // Set the attempt in the store
      setAttempt(attemptResponse);

      console.log('Setting assessment state to STARTING');
      setAssessmentState(CasemasterAssessmentStates.STARTING);
    } catch (error) {
      console.error('Error fetching casemaster problem:', error);
      snackbar.error("Uh Oh! Unable to get case master problem");
      
      // Create a fallback problem object to prevent app crash
      const fallbackProblem = {
        _id: cid || 'fallback-id',
        title: 'Case Study Challenge',
        topic: 'General Case Study',
        problem: 'This is a sample case study problem. Please analyze the situation and provide your recommendations.',
        description: `<div style="line-height: 1.6;">
          <h3 style="color: #1961BF; margin-bottom: 16px;">Topic: General Case Study</h3>
          <p style="margin-bottom: 12px;">You have <strong>30:00 mins</strong> to complete the case study</p>
          
          <div style="background-color: #F5F7FA; padding: 16px; border-radius: 8px; margin: 16px 0;">
            <h4 style="margin-top: 0; margin-bottom: 8px;">Case Introduction:</h4>
            <p style="margin: 0;">This is a sample case study problem. Please analyze the situation and provide your recommendations.</p>
          </div>
        </div>`,
        difficulty: 'Medium',
        duration: 30,
        entryPoint: data,
        otherDetails: {}
      };
      
      setCasemasterProblem(fallbackProblem);
      setTotalDuration(1800);
      
      // Create a fallback attempt
      const fallbackAttempt = {
        _id: 'fallback-attempt-id',
        user: Session.userId,
        casemasterProblem: cid || 'fallback-id',
      };
      setAttempt(fallbackAttempt);

      setAssessmentState(CasemasterAssessmentStates.STARTING);
    } finally {
      loading.hide();
    }
  }, [searchParams, loading, setCasemasterProblem, setTotalDuration, setAttempt, setAssessmentState, snackbar, data]);

  const startAttempt = useCallback(async () => {
    if (!attempt || !Object.keys(attempt).length) {
      // This should not happen now since attempt is created in fetchCasemasterProblem
      console.log('No attempt found, this should not happen');
      snackbar.error("No attempt found. Please refresh and try again.");
      return;
    }

    try {
      loading.show();
      console.log('Starting assessment with existing attempt:', attempt);

      // Call the start attempt API with current timestamp
      const startData = {
        startedAt: new Date().toISOString()
      };

      await startCaseMasterAttempt(attempt._id, startData);
      console.log('Attempt started successfully at:', startData.startedAt);

      // Now start the assessment (timer and proctoring)
      setTimeout(() => startAssessment(), 1000);
    } catch (error) {
      console.error('Error starting attempt:', error);
      snackbar.error("Unable to start the assessment. Please try again.");
    } finally {
      loading.hide();
    }
  }, [attempt, startAssessment, snackbar, loading]);

  const handleFinishAssessment = useCallback(async () => {
    if (finishing || assessmentState !== CasemasterAssessmentStates.FINISHING) return;

    try {
      setOpenFinalSubmittionDialog(false);
      setFinishing(true);
      endProctoring();
      const proctorRecords = getProctoringData(true);

      await finishCaseMasterAttempt(attempt._id, {
        finishedAt: new Date().toISOString(), 
        proctorRecords
      });

      console.log('Assessment finished successfully');

      if (!hasGivenResponse) {
        console.log('No responses given during assessment');
        reset();
        snackbar.info("Assessment ended. No responses recorded.");
        navigate(`/${getClientPrefix(user)}/personalized-interview?type=case-master`);
        return;
      }

      setFinished(true);
    } catch (error) {
      console.error('Error finishing assessment:', error);
      snackbar.error("Uh Oh! Unable to finish the attempt");
      navigate(`/${getClientPrefix(user)}/personalized-interview?type=case-master`);
    }
  }, [finishing, assessmentState, endProctoring, getProctoringData, attempt, snackbar, navigate, user, hasGivenResponse, reset]);

  const handleSuccessFullscreenMode = () => {
    enableFullscreenMode();
    setOpenFullscreenDialog(false);
    if (assessmentState === CasemasterAssessmentStates.FULL_SCREEN_PERMISSION) {
      setAssessmentState(CasemasterAssessmentStates.SHOWING_TUTORIAL);
    }
  };

  useEffect(() => {
    console.log('Assessment state changed to:', assessmentState);
    switch (assessmentState) {
      case CasemasterAssessmentStates.SHOWING_TUTORIAL: {
        console.log('Showing tutorial...');
        const shown = JSON.parse(localStorage.getItem("case-master-interview-tour"));
        if (play || openTourDialog) break;
        if (shown && !openTourDialog) endTutorial();
        else {
          setSteps(CaseMasterTourSteps);
          setOpenTourDialog(true);
          localStorage.setItem("case-master-interview-tour", "true");
        }
        break;
      }
      case CasemasterAssessmentStates.FETCHING_CASEMASTER_PROBLEM: {
        console.log('Fetching casemaster problem...');
        fetchCasemasterProblem();
        break;
      }
      case CasemasterAssessmentStates.STARTING: {
        console.log('Starting attempt...');
        startAttempt();
        break;
      }
      case CasemasterAssessmentStates.RUNNING: {
        console.log('Assessment running...');
        activateFullScreenProctoring();
        activateGeneralProctoring();
        break;
      }
      case CasemasterAssessmentStates.FULL_SCREEN_PERMISSION: {
        console.log('Requesting fullscreen permission...');
        setOpenFullscreenDialog(true);
        break;
      }
      case CasemasterAssessmentStates.FINISHING: {
        console.log('Finishing assessment...');
        handleFinishAssessment();
        break;
      }
      default:
        console.log('Unknown assessment state:', assessmentState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentState, play]);

  useEffect(() => {
    setOpenFinalSubmittionDialog(timeOut);
  }, [timeOut]);

  useEffect(() => {
    const listener = () => window.history.go(1);

    window.addEventListener('popstate', listener);
    console.log("Added popstate event");

    return () => {
      console.log("Removed popstate event");
      window.removeEventListener('popstate', listener);
      endProctoring();
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    const handleUnload = (event) => {
      if (window.location.pathname.includes("/case-master-assessment")) {
        sessionStorage.setItem('caseMasterAssessmentLeft', 'true');
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem('caseMasterAssessmentLeft') === 'true') {
      sessionStorage.removeItem('caseMasterAssessmentLeft');
      navigate(-1, { replace: true });
    }
  }, [navigate, location.pathname]);

  const handleCloseTourDialog = useCallback((value) => {
    setOpenTourDialog(value);
  }, []);

  const handleCloseActionTimer = useCallback(() => {
    setAssessmentState(CasemasterAssessmentStates.FINISHING);
  }, [setAssessmentState]);

  return finishing ? <AnalysisScreen isFeedbackGenerated={finished} /> : (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Header />
      </Box>
      <Box className={classes.content}>
        <InterviewerSection />
        <Box className={classes.quesSection}>
          <QuestionSection />
        </Box>
      </Box>

      <TourStartDialog
        open={openTourDialog}
        onClose={handleCloseTourDialog}
        showCloseButton={false}
        title="Case Study Interview Walkthrough"
        description={
          <>Before proceeding to the case study interview, it's necessary to understand how it works. This walkthrough
            <strong> will not be repeated</strong>.
            Please read instructions carefully to avoid any confusion. Good luck!
          </>
        }
        image='https://assets.languify.in/gifs/interviewScreenGif.gif'
        buttonLabel="Let's get started"
      />
      {
        openFinalSubmittionDialog &&
        <ActionTimerDialog
          open={openFinalSubmittionDialog}
          onClose={handleCloseActionTimer}
          title={`It was great talking to you, ${Session.user.name}`}
          timerMessage='Auto-submitting {timeLeft} seconds'
        />
      }

      <FullscreenPermissionDialog
        open={
          openFullscreenDialog ||
          (!fullScreenActive && assessmentState === CasemasterAssessmentStates.RUNNING)
        }
        onSuccess={handleSuccessFullscreenMode}
      />
    </Box>
  );
};

export default CaseMasterAssessment;
